import { Stack, Typography } from "@mui/material";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusButtonGroup } from "@smartrr/shared/customerPortal/components/AquariusButtonGroup";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React from "react";
import { toast } from "react-toastify";

import { CustomerAction } from "@customer-app/state/typedCustomerRedux";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { RedeemIncentiveOTPProps } from "./RedeemIncentiveOTP";
import { LoyaltyStoreAccess } from "../../../store";
import { IncentiveColoredImage } from "../../IncentiveColoredImage";

interface RedeemIncentiveDeliveryProps extends RedeemIncentiveOTPProps {
  upcomingOrder: PurchaseStateAndDelivery;
}
export const RedeemIncentiveDelivery = ({
  upcomingOrder,
  incentive,
  closeIncentivesModal,
}: RedeemIncentiveDeliveryProps) => {
  const actions = LoyaltyStoreAccess.useActions();
  const isLoading = LoyaltyStoreAccess.useIsLoading();
  const { symbol: currencySymbol } = LoyaltyStoreAccess.useCurrency();
  const incentiveCostSuffix = LoyaltyStoreAccess.useSuffix(incentive.costInPoints);
  const dispatch = useSmartrrCustomerDispatch();

  const redeemIncentive = async () => {
    const response = await actions.order.redeem.incentive(incentive);
    if (response.result === "success") {
      const info = response.data;
      if (info.orgPoints) {
        dispatch({
          type: "UPDATED_CUSTOMER_LOYALTY_POINTS",
          payload: {
            points: info.orgPoints,
          },
        } as CustomerAction<"UPDATED_CUSTOMER_LOYALTY_POINTS">);
      }
      if (info.updatedCPS) {
        dispatch({
          type: "UPDATE_CUSTOMER_PURCHASE_STATE",
          payload: {
            purchaseState: info.updatedCPS,
          },
        } as CustomerAction<"UPDATE_CUSTOMER_PURCHASE_STATE">);
      }
      toast(
        `${
          incentive.priceRule.type === "fixed_amount"
            ? `$${-incentive.priceRule.value}`
            : `${-incentive.priceRule.value}%`
        } ${typedLocalize("loyalty.addCreditToOrderToDate")}` +
          typedLocalize("utils.dates.toViewDateLong", {
            date: upcomingOrder.date,
          })
      );
    } else {
      toast.error(response.message);
    }
    closeIncentivesModal();
  };

  return (
    <React.Fragment>
      <Stack alignItems="center" justifyContent="center" py="30px" spacing="10px">
        <Typography variant="h4">
          {typedLocalize("loyalty.redeemForPoints", {
            costInPoints: incentive.costInPoints,
            unitName: incentiveCostSuffix,
          })}
        </Typography>
        <IncentiveColoredImage incentive={incentive} />
        <Typography variant="body1">
          {`${
            incentive.priceRule.type === "fixed_amount"
              ? `${currencySymbol}${-incentive.priceRule.value}`
              : `${-incentive.priceRule.value}%`
          } ${typedLocalize("loyalty.addCreditToOrderToDate")}`}
        </Typography>
        <Typography variant="body2">
          {typedLocalize("utils.dates.toViewDateLong", {
            date: upcomingOrder.date,
          })}
        </Typography>
      </Stack>
      <Stack alignItems="center" justifyContent="center" py="30px" spacing="10px"></Stack>
      <AquariusButtonGroup topBorder>
        <AquariusButton onClick={closeIncentivesModal} disabled={isLoading}>
          {typedLocalize(`general.cancelBtn`)}
        </AquariusButton>
        <AquariusButton filled onClick={async () => redeemIncentive()} isLoading={isLoading}>
          {typedLocalize(`general.confirmBtn`)}
        </AquariusButton>
      </AquariusButtonGroup>
    </React.Fragment>
  );
};
